import type { WorkOrderGetByIdResponse } from '@/types/workOrder.types';

export interface ProcessBillingAssignedServer {
  serverId: string;
  serverCode: string | null;
  displayName: string;
}

function displayName(first?: string | null, last?: string | null, serverId?: string): string {
  const joined = [first, last].filter(Boolean).join(' ').trim();
  if (joined) return joined;
  if (serverId) return serverId;
  return 'Server';
}

function readServerCode(server: unknown): string | null {
  if (!server || typeof server !== 'object') return null;
  const code = (server as { code?: string | null }).code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/** Eligible when the work order is process service and at least one server is linked on the work order or an attempt. */
export function isProcessBillingServerEligible(workOrder: WorkOrderGetByIdResponse | undefined): boolean {
  if (!workOrder || workOrder.service?.type !== 'PROCESS_SERVICE') return false;
  if (workOrder.serverId) return true;
  return Boolean(workOrder.workOrderAttempts?.some((a) => Boolean(a.serverId)));
}

export function getAssignedServersForProcessBilling(
  workOrder: WorkOrderGetByIdResponse
): ProcessBillingAssignedServer[] {
  const map = new Map<string, ProcessBillingAssignedServer>();

  if (workOrder.serverId) {
    const display = displayName(workOrder.server?.firstName, workOrder.server?.lastName, workOrder.serverId);
    map.set(workOrder.serverId, {
      serverId: workOrder.serverId,
      serverCode: readServerCode(workOrder.server),
      displayName: display,
    });
  }

  for (const att of workOrder.workOrderAttempts ?? []) {
    if (att.serverId) {
      const display = displayName(att.server?.firstName, att.server?.lastName, att.serverId);
      const existing = map.get(att.serverId);
      map.set(att.serverId, {
        serverId: att.serverId,
        serverCode: existing?.serverCode ?? readServerCode(att.server ?? null),
        displayName: existing?.displayName ?? display,
      });
    }
  }

  return [...map.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}
